import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { PhotoProvider } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import { toast } from 'sonner'
import { ImageGrid, type GridImage } from './ImageGrid'
import {
  PhotoThumbnail,
  PendingThumbnail,
  fileNameFromS3Key,
} from './TaskPhotos'
import {
  useS3GetUploadPost,
  uploadThumbnailBestEffort,
  useS3DownloadUrlsQuery,
  generateChecklistItemPhotoS3Key,
} from '@/lib/hooks/s3'
import { useTaskAddItemPhoto, useTaskRemoveItemPhoto } from '@/lib/hooks/task'
import { isNative } from '@/lib/native-file-viewer'

const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB
const ACCEPT = 'image/*'

interface ChecklistItemPhotosProps {
  taskId: string
  itemId: string
  photoS3Keys: string[]
  readOnly?: boolean
  onPendingChange?: (count: number) => void
}

export interface ChecklistItemPhotosHandle {
  flushPending: (onUploaded?: () => void) => Promise<void>
  openPicker: () => void
  pendingUploadCount: () => number
}

interface PendingPhoto {
  id: string
  file: File
  previewUrl: string
}

export const ChecklistItemPhotos = forwardRef<ChecklistItemPhotosHandle, ChecklistItemPhotosProps>(
  function ChecklistItemPhotos(
    { taskId, itemId, photoS3Keys, readOnly = false, onPendingChange },
    ref
  ) {
    const getUploadPost = useS3GetUploadPost()
    const addPhoto = useTaskAddItemPhoto()
    const removePhoto = useTaskRemoveItemPhoto()

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pending, setPending] = useState<PendingPhoto[]>([])
    const [pendingRemovals, setPendingRemovals] = useState<string[]>([])
    const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set())

    const visibleS3Keys = photoS3Keys.filter((k) => !pendingRemovals.includes(k))

    // Read-only collage: cached/deduped per key; tiles get the 400px variant,
    // the lightbox the original.
    const { urls, isPending: urlsLoading } = useS3DownloadUrlsQuery(
      readOnly ? visibleS3Keys : [],
      { variant: 'thumb' }
    )


    useEffect(() => {
      onPendingChange?.(pending.length + pendingRemovals.length)
    }, [pending.length, pendingRemovals.length, onPendingChange])

    useEffect(() => {
      return () => {
        setPending((cur) => {
          cur.forEach((p) => URL.revokeObjectURL(p.previewUrl))
          return cur
        })
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        openPicker: () => fileInputRef.current?.click(),
        pendingUploadCount: () => pending.length,
        flushPending: async (onUploaded) => {
          if (pending.length === 0 && pendingRemovals.length === 0) return
          const uploadedIds = new Set<string>()
          const removedKeys = new Set<string>()
          let firstError: unknown = null

          for (const item of pending) {
            setUploadingIds((cur) => new Set(cur).add(item.id))
            try {
              const s3Key = generateChecklistItemPhotoS3Key(taskId, itemId, item.file.name)
              const { url, fields } = await getUploadPost.mutateAsync({
                s3Key,
                contentType: item.file.type || 'application/octet-stream',
              })

              const formData = new FormData()
              Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
              formData.append('file', item.file)

              const res = await fetch(url, { method: 'POST', body: formData })
              if (!res.ok) throw new Error(`Upload failed (${res.status})`)

              await uploadThumbnailBestEffort(item.file, s3Key, getUploadPost.mutateAsync)

              await addPhoto.mutateAsync({ itemId, s3Key })
              uploadedIds.add(item.id)
              URL.revokeObjectURL(item.previewUrl)
              onUploaded?.()
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Upload failed'
              toast.error(`${item.file.name}: ${msg}`)
            } finally {
              setUploadingIds((cur) => {
                const next = new Set(cur)
                next.delete(item.id)
                return next
              })
            }
          }

          for (const s3Key of pendingRemovals) {
            try {
              await removePhoto.mutateAsync({ itemId, s3Key })
              removedKeys.add(s3Key)
            } catch (err) {
              if (!firstError) firstError = err
              const msg = err instanceof Error ? err.message : 'Failed to remove photo'
              toast.error(msg)
            }
          }

          setPending((cur) => cur.filter((p) => !uploadedIds.has(p.id)))
          setPendingRemovals((cur) => cur.filter((k) => !removedKeys.has(k)))
          if (firstError) throw firstError
        },
      }),
      [pending, pendingRemovals, taskId, itemId, getUploadPost, addPhoto, removePhoto]
    )

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      e.target.value = ''
      if (files.length === 0) return

      const accepted: PendingPhoto[] = []
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`)
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`${file.name} exceeds the 20 MB limit`)
          continue
        }
        accepted.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
        })
      }
      if (accepted.length > 0) {
        setPending((cur) => [...cur, ...accepted])
      }
    }

    const handleRemoveUploaded = (s3Key: string) => {
      setPendingRemovals((cur) => (cur.includes(s3Key) ? cur : [...cur, s3Key]))
    }

    const handleRemovePending = (id: string) => {
      setPending((cur) => {
        const next = cur.filter((p) => p.id !== id)
        const removed = cur.find((p) => p.id === id)
        if (removed) URL.revokeObjectURL(removed.previewUrl)
        return next
      })
    }

    if (readOnly) {
      if (visibleS3Keys.length === 0) return null
      const gridImages: GridImage[] = visibleS3Keys.map((k) => ({
        id: k,
        url: urls[k]?.downloadUrl ?? null,
        thumbUrl: urls[k]?.thumbnailUrl ?? null,
        name: fileNameFromS3Key(k),
      }))
      return <ImageGrid images={gridImages} loading={urlsLoading} />
    }

    const hasThumbnails = visibleS3Keys.length > 0 || pending.length > 0

    const grid = hasThumbnails ? (
      <div className="grid grid-cols-2 gap-2">
        {visibleS3Keys.map((s3Key) => (
          <PhotoThumbnail
            key={s3Key}
            s3Key={s3Key}
            onRemove={() => handleRemoveUploaded(s3Key)}
          />
        ))}
        {pending.map((p) => (
          <PendingThumbnail
            key={p.id}
            previewUrl={p.previewUrl}
            uploading={uploadingIds.has(p.id)}
            onRemove={() => handleRemovePending(p.id)}
          />
        ))}
      </div>
    ) : null

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />
        {grid && (isNative ? grid : <PhotoProvider>{grid}</PhotoProvider>)}
      </>
    )
  }
)
